import React, { useEffect, useState } from "react";
import { Row, Col, Card, Badge, Button, Modal, Form, Spinner } from "react-bootstrap";
import { FaBriefcase, FaClock, FaLaptopHouse, FaUsers, FaEdit } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAbsenceApprovers,
  fetchManagers,
  fetchMyPermissions,
  resetTeleworkLocation,
  updateUser,
} from "../../../redux/slices/userSlice";
import { fetchDelegations } from "../../../redux/slices/delegationSlice";
import { fetchRoles } from "../../../redux/slices/rolesSlice";
import { fetchTeams } from "../../../redux/slices/teamSlice";
import { fetchShiftsByUser } from "../../../redux/slices/shiftSlice";

const getErrorMessage = (error, fallback) => {
  const detail = error?.payload?.detail || error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        return field ? `${field}: ${item.msg || item.message || ""}` : item.msg || item.message || "";
      })
      .filter(Boolean)
      .join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const getPersonLabel = (person, fallback) => {
  if (!person) return fallback;
  if (typeof person === "string") return person;

  const fullName = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return fullName || person.username || fallback;
};

const DetailRow = ({ label, value }) => (
  <div className="mb-3 d-flex justify-content-between align-items-center gap-3 border-bottom pb-2">
    <span className="txt-subtle">{label}</span>
    <span className="fw-semibold txt-main text-end" style={{ overflowWrap: "anywhere", minWidth: 0 }}>
      {value}
    </span>
  </div>
);

const POSITION_OPTIONS = [
  "N/A",
  "Manager",
  "Coordinator",
  "Specialist",
  "Technician",
  "Operator",
  "Assistant",
  "Other",
];

const LaborDetailsTab = ({ currentUser, userId, onNavigateToTimePlanning }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loggedInUser = useSelector((state) => state.auth?.user);
  const permissions = useSelector((state) => state.users?.permissions || {});
  const roles = useSelector((state) => state.roles?.items || []);
  const delegations = useSelector((state) => state.delegations?.items || []);
  const teams = useSelector((state) => state.teams?.items || []);
  const { items: shifts, isLoading: shiftsLoading } = useSelector(
    (state) => state.shifts || { items: [], isLoading: false }
  );

  const isOwner = String(loggedInUser?.id) === String(userId);
  const canUpdateUsers = permissions.can_update_users === true;
  const canManageRole = permissions.can_delete_users === true;
  const canManageOrganization = canUpdateUsers && (!isOwner || canManageRole);

  const [scheduleType, setScheduleType] = useState("normal");
  const [showFlexModal, setShowFlexModal] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [savingFlexibility, setSavingFlexibility] = useState(false);
  const [resettingTelework, setResettingTelework] = useState(false);
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [managers, setManagers] = useState([]);
  const [absenceApprovers, setAbsenceApprovers] = useState([]);
  const [organizationForm, setOrganizationForm] = useState({});
  const [flexData, setFlexData] = useState({
    remote: false,
    city: "",
    country: "",
  });

  useEffect(() => {
    dispatch(fetchMyPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchShiftsByUser(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (currentUser) {
      setFlexData({
        remote: currentUser.remote || false,
        city: currentUser.city || "",
        country: currentUser.country || "",
      });
      setOrganizationForm({
        role_id: currentUser.role_id || "",
        delegation_id: currentUser.delegation_id || "",
        team_tag: currentUser.team_tag || "",
        position: currentUser.position || "",
        manager_id: currentUser.manager_id || "",
        absence_approver_id: currentUser.absence_approver_id || "",
      });
      setScheduleType(currentUser.schedule_type || "normal");
    }
  }, [currentUser]);

  useEffect(() => {
    if (!userId || !canManageOrganization) return;

    dispatch(fetchRoles());
    dispatch(fetchDelegations());
    dispatch(fetchTeams());

    dispatch(fetchManagers())
      .unwrap()
      .then((data) => setManagers(Array.isArray(data) ? data : []))
      .catch(() => setManagers([]));

    dispatch(fetchAbsenceApprovers())
      .unwrap()
      .then((data) => setAbsenceApprovers(Array.isArray(data) ? data : []))
      .catch(() => setAbsenceApprovers([]));
  }, [dispatch, userId, canManageOrganization]);

  const handleResetTelework = async () => {
    if (!window.confirm(t("components.employeeProfile.laborDetailsTab.flexibility.teleworkResetConfirm"))) return;
    setResettingTelework(true);
    try {
      await dispatch(resetTeleworkLocation(currentUser.id)).unwrap();
      toast.success(t("components.employeeProfile.laborDetailsTab.flexibility.teleworkResetSuccess"));
    } catch {
      toast.error(t("components.employeeProfile.laborDetailsTab.flexibility.teleworkResetError"));
    } finally {
      setResettingTelework(false);
    }
  };

  const handleSaveFlexibility = async () => {
    if (!userId) return;
    const updates = {};
    if (canUpdateUsers) {
      updates.remote = flexData.remote;
    } else {
      toast.info(
        t("components.employeeProfile.laborDetailsTab.errors.remoteReadOnly", {
          defaultValue:
            "O regime de trabalho é gerido por um administrador e não foi alterado.",
        })
      );
    }
    updates.city = flexData.city;
    updates.country = flexData.country;

    setSavingFlexibility(true);
    try {
      await dispatch(updateUser({ userId, updates })).unwrap();
      setShowFlexModal(false);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("components.employeeProfile.laborDetailsTab.errors.flexibilitySave", {
            defaultValue: "Não foi possível guardar a flexibilidade.",
          })
        )
      );
    } finally {
      setSavingFlexibility(false);
    }
  };

  const handleOrganizationChange = (event) => {
    const { name, value } = event.target;
    setOrganizationForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSaveOrganization = async (event) => {
    event.preventDefault();
    if (!canManageOrganization || !userId) return;

    const updates = {
      delegation_id: organizationForm.delegation_id || null,
      team_tag: organizationForm.team_tag || null,
      position: organizationForm.position || null,
      manager_id: organizationForm.manager_id || null,
      absence_approver_id: organizationForm.absence_approver_id || null,
    };

    // Role changes remain Super Admin-only in the backend.
    if (canManageRole && organizationForm.role_id) {
      updates.role_id = organizationForm.role_id;
    }

    setSavingOrganization(true);
    try {
      await dispatch(updateUser({ userId, updates })).unwrap();
      setShowOrganizationModal(false);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("components.employeeProfile.laborDetailsTab.errors.organizationSave", {
            defaultValue: "Não foi possível guardar a estrutura organizacional.",
          })
        )
      );
    } finally {
      setSavingOrganization(false);
    }
  };

  const latestShift =
    shifts && shifts.length > 0
      ? [...shifts].sort((a, b) => new Date(b.start_time) - new Date(a.start_time))[0]
      : null;

  const formatHours = (decimalHours) => {
    const hours = Number(decimalHours);
    if (!Number.isFinite(hours)) {
      return t("components.employeeProfile.laborDetailsTab.notDefined", "Not Defined");
    }

    const totalMinutes = Math.round(hours * 60);
    return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, "0")}m`;
  };

  if (!currentUser) return null;

  const notDefined = t("components.employeeProfile.laborDetailsTab.notDefined", "Not Defined");
  const delegationName =
    currentUser.delegation?.name || currentUser.delegation_name || notDefined;
  const teamName = currentUser.team_tag || notDefined;
  const positionName = currentUser.position || notDefined;
  const permissionGroup = currentUser.role?.name || notDefined;
  const managerLabel = getPersonLabel(currentUser.manager, notDefined);
  const absenceApproverLabel = currentUser.absence_approver
    ? getPersonLabel(currentUser.absence_approver, notDefined)
    : currentUser.absence_approver_id
    ? t("components.employeeProfile.laborDetailsTab.assigned", "Atribuído")
    : notDefined;

  const expectedHoursDay = latestShift?.expected_hours ?? latestShift?.total_hours ?? null;
  const workingDaysPerWeek = Number(currentUser?.working_days_per_week) || 5;
  const expectedHoursWeek = expectedHoursDay !== null ? expectedHoursDay * workingDaysPerWeek : null;

  return (
    <>
      <Row className="g-4">
        <Col md={6}>
          <Card className="atlar-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div className="d-flex align-items-center gap-2">
                  <FaBriefcase className="txt-brand" size={18} />
                  <h5 className="fw-bold txt-main m-0">
                    {t("components.employeeProfile.laborDetailsTab.organization.title", "Organização")}
                  </h5>
                </div>
                {canManageOrganization && (
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 txt-subtle"
                    onClick={() => setShowOrganizationModal(true)}
                    aria-label={t("components.employeeProfile.laborDetailsTab.organization.edit", "Editar organização")}
                  >
                    <FaEdit />
                  </Button>
                )}
              </div>

              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.organization.delegation", "Delegação")}
                value={delegationName}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.organization.team", "Equipa")}
                value={teamName}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.organization.position", "Função")}
                value={positionName}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.organization.permissionGroup", "Grupo de permissões")}
                value={
                  <Badge bg="light" text="dark" className="border">
                    {permissionGroup}
                  </Badge>
                }
              />

              <small className="txt-subtle d-block mt-2" style={{ fontSize: "0.8rem" }}>
                {t(
                  "components.employeeProfile.laborDetailsTab.organization.managementHint",
                  "A estrutura organizacional é gerida por administradores."
                )}
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="atlar-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div className="d-flex align-items-center gap-2">
                  <FaUsers className="txt-brand" size={18} />
                  <h5 className="fw-bold txt-main m-0">
                    {t("components.employeeProfile.laborDetailsTab.management.title", "Gestão e aprovação")}
                  </h5>
                </div>
                {canManageOrganization && (
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 txt-subtle"
                    onClick={() => setShowOrganizationModal(true)}
                    aria-label={t("components.employeeProfile.laborDetailsTab.management.edit", "Editar gestão")}
                  >
                    <FaEdit />
                  </Button>
                )}
              </div>

              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.management.manager", "Gestor / Reporta a")}
                value={managerLabel}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.management.absenceApprover", "Aprovador de ausências")}
                value={absenceApproverLabel}
              />

              <small className="txt-subtle d-block mt-2" style={{ fontSize: "0.8rem" }}>
                {t(
                  "components.employeeProfile.laborDetailsTab.management.hint",
                  "O gestor e o aprovador são definidos por administradores."
                )}
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="atlar-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div className="d-flex align-items-center gap-2">
                  <FaClock className="txt-brand" size={18} />
                  <h5 className="fw-bold txt-main m-0">
                    {t("components.employeeProfile.laborDetailsTab.schedule.title")}
                  </h5>
                </div>
                {canManageOrganization && (
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 txt-subtle"
                    onClick={onNavigateToTimePlanning}
                    aria-label={t("components.employeeProfile.laborDetailsTab.schedule.edit", "Editar horário")}
                  >
                    <FaEdit />
                  </Button>
                )}
              </div>

              {shiftsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <>
                  <DetailRow
                    label={t("components.employeeProfile.laborDetailsTab.schedule.expectedHoursDay")}
                    value={
                      expectedHoursDay !== null
                        ? formatHours(expectedHoursDay)
                        : t("components.employeeProfile.laborDetailsTab.notDefined", "Not Defined")
                    }
                  />
                  <DetailRow
                    label={t("components.employeeProfile.laborDetailsTab.schedule.expectedHoursWeek")}
                    value={
                      expectedHoursWeek !== null
                        ? formatHours(expectedHoursWeek)
                        : t("components.employeeProfile.laborDetailsTab.notDefined", "Not Defined")
                    }
                  />
                  <DetailRow
                    label={t("components.employeeProfile.laborDetailsTab.schedule.scheduleType")}
                    value={
                      <Badge bg="light" text="dark" className="border">
                        {scheduleType === "shift"
                          ? t("components.employeeProfile.laborDetailsTab.schedule.types.shift")
                          : t("components.employeeProfile.laborDetailsTab.schedule.types.normal")}
                      </Badge>
                    }
                  />
                </>
              )}

              <div className="txt-subtle mt-3" style={{ fontSize: "0.8rem" }}>
                {t(
                  "components.employeeProfile.laborDetailsTab.schedule.managedElsewhere",
                  "As configurações de horário e turnos são geridas noutro separador."
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="atlar-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div className="d-flex align-items-center gap-2">
                  <FaLaptopHouse className="txt-brand" size={20} />
                  <h5 className="fw-bold txt-main m-0">
                    {t("components.employeeProfile.laborDetailsTab.flexibility.title")}
                  </h5>
                </div>
                {canManageOrganization && (
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 txt-subtle"
                    onClick={() => setShowFlexModal(true)}
                    aria-label={t("components.employeeProfile.laborDetailsTab.flexibility.edit", "Editar flexibilidade")}
                  >
                    <FaEdit />
                  </Button>
                )}
              </div>

              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.flexibility.workModel")}
                value={
                  <Badge bg={currentUser.remote ? "info" : "secondary"} className="text-white">
                    {currentUser.remote
                      ? t("components.employeeProfile.laborDetailsTab.flexibility.remote")
                      : t("components.employeeProfile.laborDetailsTab.flexibility.onsite")}
                  </Badge>
                }
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.flexibility.baseLocation")}
                value={currentUser.city || notDefined}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.flexibility.country")}
                value={currentUser.country || notDefined}
              />
              <DetailRow
                label={t("components.employeeProfile.laborDetailsTab.flexibility.teleworkLocation")}
                value={
                  <span className="d-inline-flex align-items-center gap-2">
                    {currentUser.telework_location_set
                      ? t("components.employeeProfile.laborDetailsTab.flexibility.teleworkSet")
                      : t("components.employeeProfile.laborDetailsTab.flexibility.teleworkNotSet")}
                    {canUpdateUsers && currentUser.telework_location_set && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        disabled={resettingTelework}
                        onClick={handleResetTelework}
                      >
                        {t("components.employeeProfile.laborDetailsTab.flexibility.teleworkReset")}
                      </Button>
                    )}
                  </span>
                }
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showOrganizationModal}
        onHide={() => !savingOrganization && setShowOrganizationModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: "1.1rem" }}>
            {t("components.employeeProfile.laborDetailsTab.organization.modalTitle", "Estrutura organizacional")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveOrganization}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.organization.permissionGroup", "Grupo de permissões")}
                  </Form.Label>
                  <Form.Select
                    name="role_id"
                    value={organizationForm.role_id || ""}
                    onChange={handleOrganizationChange}
                    disabled={!canManageRole}
                    required={canManageRole}
                  >
                    <option value="">{t("common.select", "Selecione...")}</option>
                    {roles.map((roleOption) => (
                      <option key={roleOption.id} value={roleOption.id}>
                        {roleOption.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.organization.delegation", "Delegação")}
                  </Form.Label>
                  <Form.Select
                    name="delegation_id"
                    value={organizationForm.delegation_id || ""}
                    onChange={handleOrganizationChange}
                  >
                    <option value="">
                      {t("components.OtherDetailsTab.noDelegation", "Sem delegação")}
                    </option>
                    {delegations.map((delegation) => (
                      <option key={delegation.id} value={delegation.id}>
                        {delegation.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.organization.team", "Equipa")}
                  </Form.Label>
                  <Form.Select
                    name="team_tag"
                    value={organizationForm.team_tag || ""}
                    onChange={handleOrganizationChange}
                  >
                    <option value="">
                      {t("components.OtherDetailsTab.noTeam", "Sem equipa")}
                    </option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.organization.position", "Função")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    list="labor-position-options"
                    value={organizationForm.position || ""}
                    onChange={handleOrganizationChange}
                  />
                  <datalist id="labor-position-options">
                    {POSITION_OPTIONS.map((position) => (
                      <option key={position} value={position} />
                    ))}
                    {organizationForm.position &&
                      !POSITION_OPTIONS.includes(organizationForm.position) && (
                        <option value={organizationForm.position} />
                      )}
                  </datalist>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.management.manager", "Gestor / Reporta a")}
                  </Form.Label>
                  <Form.Select
                    name="manager_id"
                    value={organizationForm.manager_id || ""}
                    onChange={handleOrganizationChange}
                  >
                    <option value="">
                      {t("components.OtherDetailsTab.noManager", "Sem gestor")}
                    </option>
                    {managers
                      .filter((manager) => String(manager.id) !== String(userId))
                      .map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {getPersonLabel(manager, manager.username)}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    {t("components.employeeProfile.laborDetailsTab.management.absenceApprover", "Aprovador de ausências")}
                  </Form.Label>
                  <Form.Select
                    name="absence_approver_id"
                    value={organizationForm.absence_approver_id || ""}
                    onChange={handleOrganizationChange}
                  >
                    <option value="">
                      {t("components.OtherDetailsTab.noApprover", "Sem aprovador")}
                    </option>
                    {absenceApprovers
                      .filter((approver) => String(approver.id) !== String(userId))
                      .map((approver) => (
                        <option key={approver.id} value={approver.id}>
                          {getPersonLabel(approver, approver.username)}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="light"
              onClick={() => setShowOrganizationModal(false)}
              disabled={savingOrganization}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              style={{ backgroundColor: "var(--brand-primary)", border: "none" }}
              disabled={savingOrganization || (canManageRole && !organizationForm.role_id)}
            >
              {savingOrganization ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("common.saving", "A guardar...")}
                </>
              ) : (
                t("common.save_changes", "Guardar alterações")
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showFlexModal}
        onHide={() => !savingFlexibility && setShowFlexModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: "1.1rem" }}>
            {t("components.employeeProfile.laborDetailsTab.flexibility.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => event.preventDefault()}>
            <Form.Group className="mb-3">
              <Form.Label className="txt-subtle fw-semibold">
                {t("components.employeeProfile.laborDetailsTab.flexibility.workModel")}
              </Form.Label>
              <Form.Select
                value={flexData.remote ? "remote" : "onsite"}
                disabled={!canUpdateUsers}
                onChange={(event) =>
                  setFlexData({ ...flexData, remote: event.target.value === "remote" })
                }
              >
                <option value="onsite">
                  {t("components.employeeProfile.laborDetailsTab.flexibility.onsite")}
                </option>
                <option value="remote">
                  {t("components.employeeProfile.laborDetailsTab.flexibility.remote")}
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="txt-subtle fw-semibold">
                {t("components.employeeProfile.laborDetailsTab.flexibility.baseLocation")}
              </Form.Label>
              <Form.Control
                type="text"
                value={flexData.city}
                onChange={(event) => setFlexData({ ...flexData, city: event.target.value })}
                placeholder={t(
                  "components.employeeProfile.laborDetailsTab.flexibility.baseLocationPlaceholder",
                  "Ex: Lisboa, Aveiro..."
                )}
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label className="txt-subtle fw-semibold">
                {t("components.employeeProfile.laborDetailsTab.flexibility.country")}
              </Form.Label>
              <Form.Control
                type="text"
                value={flexData.country}
                onChange={(event) => setFlexData({ ...flexData, country: event.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="light"
            onClick={() => setShowFlexModal(false)}
            disabled={savingFlexibility}
          >
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            style={{ backgroundColor: "var(--brand-primary)", border: "none" }}
            onClick={handleSaveFlexibility}
            disabled={savingFlexibility}
          >
            {savingFlexibility ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("common.saving", "A guardar...")}
              </>
            ) : (
              t("common.save", "Guardar")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LaborDetailsTab;
